import { useEffect, useRef, useState } from 'react';
import type L from 'leaflet';
import type maplibregl from 'maplibre-gl';
import { formatDistance } from './mapGeometry';
import { haversineMetersFlat } from './gpsUtils';

/**
 * UX14: avstånd till en punkt. När man drar kartan (inbäddad eller i helskärm, 2D eller 3D) visas ett hårkors
 * mitt på kartan och en linje från min position dit, med avståndet längs linjen. Ligger kvar en stund efter att
 * man släppt, eller tills man trycker på kartan. Utan position just nu mäts från senast kända position, annars
 * från markens mitt – och det står då vid hårkorset, så att det inte läses som mitt eget avstånd.
 * Avståndsringarna påverkas inte.
 */

/** Så länge linjen ligger kvar efter att man släppt kartan. */
export const CENTER_DISTANCE_LINGER_MS = 4000;

/** Var linjen börjar: min position, senast kända position eller markens mitt. */
export type CenterDistanceOrigin = 'me' | 'last' | 'mark';

export interface CenterDistanceState {
  /** Linjens början och hårkorset, i fönstrets koordinater. */
  from: { x: number; y: number };
  to: { x: number; y: number };
  meters: number;
  origin: CenterDistanceOrigin;
  /** Kartans ruta i fönstret – lagret klipps till den (den inbäddade kartan kan ligga delvis under menyerna). */
  clip: { x: number; y: number; w: number; h: number };
}

/** Texten vid hårkorset när linjen inte börjar i min position just nu. */
export function originCaption(origin: CenterDistanceOrigin): string | null {
  return origin === 'last' ? 'från senast kända position' : origin === 'mark' ? 'från markens mitt' : null;
}

/** Vinkeln (grader) för texten längs linjen, alltid läsbar (inte upp och ned). */
export function labelAngle(x1: number, y1: number, x2: number, y2: number): number {
  let a = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  if (a > 90) a -= 180;
  if (a < -90) a += 180;
  return a;
}

type LatLng = { lat: number; lng: number };

/** En punkt i elementets egna koordinater → fönstret, med elementets CSS-transform (vridning runt mitten). */
function transformedPoint(el: HTMLElement, r: DOMRect, p: { x: number; y: number }): { x: number; y: number } {
  const t = getComputedStyle(el).transform;
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  if (!t || t === 'none' || typeof DOMMatrix === 'undefined') return { x: r.left + p.x, y: r.top + p.y };
  const m = new DOMMatrix(t);
  const dx = p.x - el.offsetWidth / 2, dy = p.y - el.offsetHeight / 2;
  return { x: cx + m.a * dx + m.c * dy, y: cy + m.b * dx + m.d * dy };
}

/**
 * Lyssnar på den platta (Leaflet) eller 3D-kartan (MapLibre) och ger linjens läge medan man drar.
 * `enabled` false (kompassläget, som följer positionen) döljer den. `fallback`: senast kända position eller
 * markens mitt, när `me` saknas.
 */
export function useCenterDistance({ enabled, me, fallback, leaflet, map3d }: {
  enabled: boolean;
  me: LatLng | null;
  fallback: () => { pos: LatLng; origin: Exclude<CenterDistanceOrigin, 'me'> } | null;
  leaflet: L.Map | null;
  map3d: maplibregl.Map | null;
}): CenterDistanceState | null {
  const [state, setState] = useState<CenterDistanceState | null>(null);
  const meRef = useRef(me);
  meRef.current = me;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const on = enabled && !!(map3d || leaflet);

  useEffect(() => {
    if (!on) { setState(null); return; }
    let active = false;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let raf = 0;
    const container = map3d ? map3d.getContainer() : leaflet!.getContainer();
    const compute = () => {
      raf = 0;
      if (!active) return;
      const fb = meRef.current ? null : fallbackRef.current();
      const pos = meRef.current ?? fb?.pos;
      if (!pos) return;
      const r = container.getBoundingClientRect();
      const to = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      let px: { x: number; y: number };
      let c: LatLng;
      if (map3d) {
        const p = map3d.project([pos.lng, pos.lat]);
        px = { x: r.left + p.x, y: r.top + p.y };
        const cc = map3d.getCenter();
        c = { lat: cc.lat, lng: cc.lng };
      } else {
        const p = leaflet!.latLngToContainerPoint([pos.lat, pos.lng]);
        // Kompassläget vrider den platta kartan med CSS (runt mitten): räkna punkten genom samma vridning.
        px = transformedPoint(container, r, p);
        c = leaflet!.getCenter();
      }
      setState({ from: px, to, meters: haversineMetersFlat(pos, c), origin: fb?.origin ?? 'me', clip: { x: r.left, y: r.top, w: r.width, h: r.height } });
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(compute); };
    const show = () => {
      active = true;
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      schedule();
    };
    const linger = () => {
      if (!active) return;
      schedule();
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(hide, CENTER_DISTANCE_LINGER_MS);
    };
    const hide = () => {
      active = false;
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      setState(null);
    };
    const onMove = () => { if (active) schedule(); };
    if (map3d) {
      map3d.on('dragstart', show); map3d.on('move', onMove); map3d.on('moveend', linger); map3d.on('click', hide);
    } else {
      leaflet!.on('dragstart', show); leaflet!.on('move', onMove); leaflet!.on('moveend', linger); leaflet!.on('click', hide);
    }
    return () => {
      if (map3d) {
        map3d.off('dragstart', show); map3d.off('move', onMove); map3d.off('moveend', linger); map3d.off('click', hide);
      } else {
        leaflet!.off('dragstart', show); leaflet!.off('move', onMove); leaflet!.off('moveend', linger); leaflet!.off('click', hide);
      }
      if (hideTimer) clearTimeout(hideTimer);
      cancelAnimationFrame(raf);
      setState(null);
    };
  }, [on, leaflet, map3d]);

  return on ? state : null;
}

/** Hårkorset, linjen och avståndet. Ett fast lager över kartan som inte tar emot tryck. */
export function CenterDistanceOverlay({ state, zIndex, color }: { state: CenterDistanceState; zIndex: number; color: string }) {
  const { from, to, meters, origin, clip } = state;
  const caption = originCaption(origin);
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  const angle = labelAngle(from.x, from.y, to.x, to.y);
  // Längs linjen om den är lång nog och mitten syns i kartan, annars vid hårkorset.
  const midInside = mid.x > clip.x + 24 && mid.x < clip.x + clip.w - 24 && mid.y > clip.y + 24 && mid.y < clip.y + clip.h - 24;
  const long = Math.hypot(to.x - from.x, to.y - from.y) > 60 && midInside;
  const halo = 'rgba(255,255,255,0.95)';
  return (
    <svg aria-hidden data-testid="center-distance" data-origin={origin} style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex, pointerEvents: 'none', clipPath: `inset(${Math.max(0, clip.y)}px calc(100% - ${clip.x + clip.w}px) calc(100% - ${clip.y + clip.h}px) ${Math.max(0, clip.x)}px)` }}>
      <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={halo} strokeWidth={6} strokeLinecap="round" />
      <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={color} strokeWidth={3} strokeLinecap="round" strokeDasharray="8 6" />
      {/* Hårkorset */}
      <g stroke={halo} strokeWidth={5} strokeLinecap="round">
        <line x1={to.x - 16} y1={to.y} x2={to.x - 5} y2={to.y} /><line x1={to.x + 5} y1={to.y} x2={to.x + 16} y2={to.y} />
        <line x1={to.x} y1={to.y - 16} x2={to.x} y2={to.y - 5} /><line x1={to.x} y1={to.y + 5} x2={to.x} y2={to.y + 16} />
      </g>
      <g stroke={color} strokeWidth={2.5} strokeLinecap="round">
        <line x1={to.x - 16} y1={to.y} x2={to.x - 5} y2={to.y} /><line x1={to.x + 5} y1={to.y} x2={to.x + 16} y2={to.y} />
        <line x1={to.x} y1={to.y - 16} x2={to.x} y2={to.y - 5} /><line x1={to.x} y1={to.y + 5} x2={to.x} y2={to.y + 16} />
      </g>
      <circle cx={to.x} cy={to.y} r={2.5} fill={color} stroke={halo} strokeWidth={1.5} />
      {/* Avståndet längs linjen (vid hårkorset när linjen är kort) */}
      <text
        x={long ? mid.x : to.x} y={long ? mid.y : to.y - 22}
        transform={long ? `rotate(${angle} ${mid.x} ${mid.y}) translate(0 -9)` : undefined}
        textAnchor="middle" fontSize={15} fontWeight={800} fill={color}
        stroke={halo} strokeWidth={4} paintOrder="stroke" style={{ fontFamily: 'system-ui, sans-serif' }}
      >{formatDistance(meters)}</text>
      {caption && (
        <text x={to.x} y={to.y + 34} textAnchor="middle" fontSize={12} fontWeight={700} fill="#333"
          stroke={halo} strokeWidth={4} paintOrder="stroke" style={{ fontFamily: 'system-ui, sans-serif' }}>{caption}</text>
      )}
    </svg>
  );
}

